<template>
    <v-navigation-drawer
        app
        width="380"
        permanent
        class="incident-drawer"
    >
      <div class="pa-4">

        <!-- Header -->
        <div class="d-flex align-start justify-space-between mb-2">
          <div>
            <div class="text-overline text-info">Incident</div>
            <div class="text-h6 font-weight-medium">
              {{ profileName }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ properties.id }}
            </div>
          </div>

          <v-icon color="info" icon="mdi-alert-circle-outline" />
        </div>

        <v-divider class="my-4" />

        <!-- Timeline -->
        <div class="text-subtitle-2 text-info mb-2">
          Timeline
        </div>

        <v-card
            variant="tonal"
            color="info"
            class="pa-3 mb-4"
        >
          <div class="d-flex flex-column ga-2">
            <div class="d-flex justify-space-between">
              <span class="text-caption">Start</span>
              <v-tooltip location="end">
                <template #activator="{ props }">
                  <span v-bind="props" class="text-body-2">
                    {{ formattedStartTime }}
                  </span>
                </template>
                <span>{{ properties.start_time }}</span>
              </v-tooltip>
            </div>

            <div class="d-flex justify-space-between">
              <span class="text-caption">End</span>
              <v-tooltip location="end">
                <template #activator="{ props }">
                  <span v-bind="props" class="text-body-2">
                    {{ formattedEndTime }}
                  </span>
                </template>

                <span>{{ properties.end_time }}</span>
              </v-tooltip>
            </div>

            <div class="d-flex justify-space-between align-center">
              <span class="text-caption">Duration</span>
              <v-chip
                  size="small"
                  color="info"
                  label
                  variant="elevated"
              >
                {{ duration }}
              </v-chip>
            </div>
          </div>
        </v-card>
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="text-subtitle-2 text-info">
            Vessels involved
          </div>
          <v-chip
              size="small"
              color="info"
              label
              variant="tonal"
          >
            {{ vesselsWithAnomalies.length }}
          </v-chip>
        </div>

        <v-list
            density="compact"
            nav
            class="px-0"
        >
          <v-list-item
              v-for="vessel in vesselsWithAnomalies"
              :key="vessel.mmsi"
              rounded="lg"
              :active="highlightMmsi === vessel.mmsi"
              active-color="info"
              @click="showAnomaliesForVessel(vessel.mmsi)"
          >
            <template #prepend>
              <v-avatar
                  size="32"
                  color="info"
                  variant="tonal"
              >
                <v-icon icon="mdi-ferry" />
              </v-avatar>
            </template>

            <v-list-item-title class="font-weight-medium">
              {{ vessel.name || 'Unknown vessel' }}
            </v-list-item-title>

            <v-list-item-subtitle>
              MMSI {{ vessel.mmsi }}
            </v-list-item-subtitle>

            <template #append>
              <v-tooltip location="end">
                <template #activator="{ props }">
                  <v-chip
                      v-bind="props"
                      size="x-small"
                      color="info"
                      label
                      variant="tonal"
                  >
                    {{ vessel.anomaliesLast24hrs }} · 24h
                  </v-chip>
                </template>
                <span>
                  Number of anomalies detected in the 24 hours preceding the incident
                </span>
              </v-tooltip>
            </template>
          </v-list-item>

          <v-list-item v-if="!vesselsWithAnomalies.length">
            <v-list-item-title class="text-medium-emphasis">
              No vessels found
            </v-list-item-title>
          </v-list-item>
        </v-list>

      </div>
    </v-navigation-drawer>
    <v-main>
      <IncidentMap
          :incident="incident"
          :anomalies="anomalies"
          :highlightMmsi="highlightMmsi"
          :exclusion-areas="exclusionAreas"
          @mapLoaded="mapLoaded"
      />
    </v-main>
</template>


<script>

import IncidentMap from "@/components/IncidentMap";
import authorizedFetch from "@/services/AuthorizedFetch";
export default {
  name: 'IncidentView',
  props: ["id"],
  components: {
    IncidentMap
  },
  data() {
    return {
      incident: {},
      properties: {},
      profileName: '',
      vessels: [],
      vesselsWithAnomalies: [],
      anomalies: {
        'type': 'FeatureCollection',
        'features': []
      },
      numberOfConsecutives: 3,
      highlightMmsi: 0,
      exclusionAreas: {
        'type': 'FeatureCollection',
        'features': []
      }
    }
  },
  methods: {
    async loadIncident(incidentId) {
      const res  = await authorizedFetch(`/api/incident/id/${incidentId}`)
      const data = await res.json()
      Object.assign(this.incident, data)
      Object.assign(this.properties, data.properties)
      await authorizedFetch(`/api/incident/profile/${this.properties.profile}`)
          .then(res => res.json())
          .then(data => this.profileName = data.name);
    },
    async loadVessels(incidentId) {
      authorizedFetch('/api/incident/neighbours/' + incidentId)
          .then(response => response.json())
          .then((data) => {
            this.vessels = data
          })
    },
    async loadAnomalies(incidentId) {
      console.log("loading anomalies")
      authorizedFetch('/api/incident/anomalies/geojson/' + incidentId)
        .then(response => response.json())
        .then((data) => {
            let features = []
            for (let i = 0; i < data.length; i++) {
              try {
                let feature = JSON.parse(data[i])

                let coordinates = feature.geometry.coordinates
                if (coordinates[0] > 180) {
                  feature.geometry = {
                    'type': feature.properties.valid_geom.type,
                    'coordinates': feature.properties.valid_geom.coordinates
                  }
                }
                features.push(feature)
              } catch (e) {
                console.log(e)
              }
            }
            console.log(features)
            this.anomalies = {
              'type': 'FeatureCollection',
              'features': features
            }
        })
    },
    async getExclusionAreas() {
      authorizedFetch('/api/incident/exclusion-areas')
          .then((response) => response.json())
          .then((data) => {
            let features = []

            for (let i = 0; i < data.length; i++) {
              try {
                let feature = JSON.parse(data[i])
                features.push(feature)
              } catch (e) {
                console.log(e)
              }
            }
            this.exclusionAreas = {
              'type': 'FeatureCollection',
              'features': features
            }
          })
    },
    showAnomaliesForVessel(mmsi) {
      this.highlightMmsi = mmsi
    },
    mapLoaded() {
      console.log('map is loaded')
      this.loadAnomalies(this.id)
      this.getExclusionAreas();
    },
    formatDateTime(value) {
      if (!value) return '—'

      const date = new Date(value)

      return new Intl.DateTimeFormat('nb-NO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Oslo'
      }).format(date)
    }
  },
  created() {
    this.loadIncident(this.id)
    this.loadVessels(this.id)
  },
  watch: {
    vessels(newVessels) {
      newVessels.forEach(vessel => {
        authorizedFetch('/api/anomaly/mmsi/' + vessel.mmsi + '/' + this.numberOfConsecutives + '/' + this.properties.start_time + '/24')
            .then(response => response.json())
            .then((data) => {
              let vesselWithAnomalies = {
                'mmsi': vessel.mmsi,
                'name': vessel.vesselName,
                'anomaliesLast24hrs': data
              }

              this.vesselsWithAnomalies.push(vesselWithAnomalies)
            })
      })
    },
    id: {
      handler(newId) {
        if (!newId) return
        console.log('loading incident: ' + newId)
        this.loadIncident(newId)
      },
      immediate: true
    }
  },
  computed: {
    duration() {
      const startTime = new Date(this.properties.start_time);
      const endTime = new Date(this.properties.end_time);
      const durationMs = Math.abs(endTime - startTime);

      let minutes = Math.floor((durationMs / (1000 * 60)) % 60);
      let hours = Math.floor((durationMs / (1000 * 60 * 60)) % 24);

      hours = hours < 10 ? "0" + hours : hours;
      minutes = minutes < 10 ? "0" + minutes : minutes;


      return hours + " hours " + minutes + " minutes";
    },
    formattedStartTime() {
      return this.formatDateTime(this.properties.start_time)
    },
    formattedEndTime() {
      return this.formatDateTime(this.properties.end_time)
    }
  }
}
</script>
<style scoped>
.incident-drawer {
  border-right: 1px solid rgba(var(--v-theme-info), 0.15);
}
</style>